import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  fallback;

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.p2pBets)) return payload.p2pBets;
  return [];
};

export const fetchUserP2PForRequests = createAsyncThunk(
  "requests/fetchUserP2PForRequests",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/p2p/user/${userId}`);
      return unwrapList(res.data);
    } catch (error) {
      if (error.response?.status === 404) return [];
      return rejectWithValue(apiMessage(error, "Could not load your P2P bets"));
    }
  }
);

export const fetchMarketP2PForRequests = createAsyncThunk(
  "requests/fetchMarketP2PForRequests",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/p2p", { params: { page: 1, limit: 100 } });
      const data = res.data;
      if (Array.isArray(data?.p2pBets)) return data.p2pBets;
      if (Array.isArray(data?.data)) return data.data;
      return unwrapList(data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load P2P requests"));
    }
  }
);

export const fetchAllRequests = createAsyncThunk(
  "requests/fetchAllRequests",
  async (userId, { dispatch, rejectWithValue }) => {
    const results = await Promise.all([
      dispatch(fetchUserP2PForRequests(userId)),
      dispatch(fetchMarketP2PForRequests()),
    ]);

    const errors = results
      .filter(
        (result) =>
          fetchUserP2PForRequests.rejected.match(result) ||
          fetchMarketP2PForRequests.rejected.match(result)
      )
      .map((result) => result.payload);

    if (errors.length === results.length) {
      return rejectWithValue(errors[0] || "Could not load requests");
    }

    return { userId, partial: errors.length > 0 };
  }
);

const requestsSlice = createSlice({
  name: "requests",
  initialState: {
    userP2PBets: [],
    marketP2PBets: [],
    status: "idle",
    error: null,
    actionLoading: false,
    actionError: null,
    lastMessage: null,
  },
  reducers: {
    clearRequestsFeedback(state) {
      state.actionError = null;
      state.lastMessage = null;
    },
    updateUserP2PBet(state, action) {
      const updated = action.payload;
      if (!updated?._id) return;
      const idx = state.userP2PBets.findIndex(
        (b) => String(b._id) === String(updated._id)
      );
      if (idx !== -1) state.userP2PBets[idx] = updated;
    },
    removeUserP2PBet(state, action) {
      const id = String(action.payload || "");
      state.userP2PBets = state.userP2PBets.filter((b) => String(b._id) !== id);
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state) => {
      state.status = "loading";
      state.error = null;
    };
    const setFailed = (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    };

    builder
      .addCase(fetchAllRequests.pending, setLoading)
      .addCase(fetchAllRequests.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(fetchAllRequests.rejected, setFailed)

      .addCase(fetchUserP2PForRequests.fulfilled, (state, action) => {
        state.userP2PBets = action.payload || [];
      })
      .addCase(fetchMarketP2PForRequests.fulfilled, (state, action) => {
        state.marketP2PBets = action.payload || [];
      });
  },
});

export const { clearRequestsFeedback, updateUserP2PBet, removeUserP2PBet } =
  requestsSlice.actions;
export default requestsSlice.reducer;
